'use client';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';

import { Product } from '@/shared/types/common';
import { Tabs, TabsItemCard } from '@/shared/ui/tabs';

import styles from './select-pet-properties.module.scss';

type SelectPetPropertiesFeatureProps = {
  products: Product[];
  href: string;
  itemType: string;
  onTabChange: (product: Product) => void;
};

export const sortProperties = (
  products: Product[],
  sorting?: string,
): Product[] => {
  const getSortValue = (product: Product): number => {
    switch (sorting) {
      case 'cheaper':
        return product.price ?? Infinity;
      case 'expensive':
        return -(product.price ?? 0);
      case 'popular':
        return -(product.h ?? 0);
      case 'relevance':
        return -(product.r ?? 0);
      default:
        return product.price ?? Infinity;
    }
  };

  const sorted = [...products].sort(
    (a, b) => getSortValue(a) - getSortValue(b),
  );

  const available = sorted.filter(p => p.price !== null);
  const soldOut = sorted.filter(p => p.price === null);

  const commonItems = available.filter(
    item => item.pumping === 'default' && !item.flyable && !item.rideable,
  );
  const bestCommonItem = commonItems[0];

  const getPropertySet = (item: Product) => ({
    flyable: item.flyable,
    rideable: item.rideable,
    pumping: item.pumping,
  });

  const propertySets = new Map<string, Product>();
  available.forEach(item => {
    const key = JSON.stringify(getPropertySet(item));
    if (!propertySets.has(key)) {
      propertySets.set(key, item);
    }
  });

  const topItems: Product[] = [];

  if (bestCommonItem) {
    topItems.push(bestCommonItem);
  }

  const propertySetItems = Array.from(propertySets.values());
  propertySetItems.forEach(item => {
    if (!topItems.find(t => t.id === item.id)) {
      topItems.push(item);
    }
  });

  const limitedTopItems = topItems.slice(0, 3);

  const remainingAvailable = available.filter(
    p => !limitedTopItems.find(item => item.id === p.id),
  );

  const remainingSoldOut = soldOut.filter(
    p => !limitedTopItems.find(item => item.id === p.id),
  );

  let final: Product[] = [...limitedTopItems];

  if (final.length < 3 && remainingAvailable.length > 0) {
    const neededFromAvailable = 3 - final.length;
    final = [...final, ...remainingAvailable.slice(0, neededFromAvailable)];
  }

  if (final.length < 3 && remainingSoldOut.length > 0) {
    const neededFromSoldOut = 3 - final.length;
    final = [...final, ...remainingSoldOut.slice(0, neededFromSoldOut)];
  }

  return final.sort((a, b) => getSortValue(a) - getSortValue(b));
};

export const SelectPetPropertiesFeature = observer(
  ({
    products,
    itemType,
    href,
    onTabChange,
  }: SelectPetPropertiesFeatureProps) => {
    const tabs =
      Array?.isArray(products) && products.length > 0 ? products : [];

    const router = useRouter();

    return (
      Array.isArray(tabs) && (
        <Tabs<string | number>
          defaultActiveTab={products[0]?.id}
          onTabsChange={id => {
            onTabChange(products.find(prod => prod.id === id)!);
          }}
        >
          {itemType === 'pet' &&
            tabs.map((tab, idx) => {
              return (
                <TabsItemCard
                  key={`tab.value-${idx}`}
                  className={cn(styles['tab-item'])}
                  value={tab?.id}
                  tagSize={18}
                  flyable={tab?.flyable!}
                  rideable={tab?.rideable!}
                  pumping={tab?.pumping!}
                />
              );
            })}

          {(itemType === 'transport' || itemType === 'egg') && (
            <TabsItemCard
              className={cn(styles['tab-item'])}
              value={'default'}
              onClick={() => router.push(href)}
              tagSize={18}
              pumping={'default'}
            />
          )}
        </Tabs>
      )
    );
  },
);
